import type React from 'react'
import { getBaseTierOfferWithJoiningFee } from 'selectors/luxPlusSelectors'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { LuxPlusPartnerPromoClassifier } from 'luxPlus/constants/partnerPromo'
import LuxPlusTuroCampaignModal from 'luxPlus/components/PartnerPromoCampaign/TuroCampaign/LuxPlusTuroCampaignModal'
import HelloFreshCampaignModal from 'luxPlus/components/PartnerPromoCampaign/HelloFreshCampaign/HelloFreshCampaignModal'
import TuroLogo from 'luxPlus/components/PartnerPromoCampaign/TuroCampaign/TuroLogo'
import HelloFreshLogo from 'luxPlus/components/PartnerPromoCampaign/HelloFreshCampaign/HelloFreshLogo'
import WineSelectorsLogo from 'luxPlus/components/PartnerPromoCampaign/WineSelectorsCampaign/WineSelectorsLogo'
import WineSelectorsCampaignModal from 'luxPlus/components/PartnerPromoCampaign/WineSelectorsCampaign/WineSelectorsCampaignModal'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

export interface PartnerPromoOffer {
  id: string
  title: string
  shortTitle: string
  description?: string
  logo: React.ReactNode
  ctaLabel: string
  onViewDetails?: () => void
}

const PARTNER_LOGO_MAP: Record<string, React.ReactNode> = {
  [LuxPlusPartnerPromoClassifier.TURO]: <TuroLogo />,
  [LuxPlusPartnerPromoClassifier.HELLOFRESH]: <HelloFreshLogo />,
  [LuxPlusPartnerPromoClassifier.WINE_SELECTORS]: <WineSelectorsLogo />,
}

/**
 * Pulls partner promo offers from the subscription product benefits and
 * matches them with the logged-in member's benefits (when available) to
 * provide modal handlers with real code/expiration data.
 */
function usePartnerPromoOffers(): Array<PartnerPromoOffer> {
  const intl = useIntl()
  const formatCurrency = useCurrencyFormatter()
  const showModal = useContext(ModalContext)
  const canRedeemPartnerOffers = useAppSelector(getCanRedeemLuxPlusBenefits)
  const { handleBecomeMember } = useLuxPlusLandingActions()

  const partnerContentOverrides = useMemo(() => ({
    [LuxPlusPartnerPromoClassifier.TURO]: {
      title: intl.formatMessage(
        {
          id: 'V-9MmT0RM949sRUklcsJ',
          defaultMessage: '{discountValue} off promo code',
          description: 'Partner promo offer title for Turo car sharing discount in LuxPlus benefits',
        },
        { discountValue: formatCurrency(100, 'roundedDollar') },
      ),
      description: intl.formatMessage({
        id: 'LnFF3ghuvqLt82MKGyqn',
        defaultMessage: 'Hit the road with the world’s largest car sharing marketplace.',
        description: 'Partner promo offer description for Turo car sharing in LuxPlus benefits',
      }),
    },
    [LuxPlusPartnerPromoClassifier.HELLOFRESH]: {
      title: intl.formatMessage(
        {
          id: '-62RwaNGuzAodHO3M5j5',
          defaultMessage: 'Up to {discountValue} off 10 boxes',
          description: 'Partner promo offer title for HelloFresh meal kit discount in LuxPlus benefits',
        },
        { discountValue: formatCurrency(419, 'roundedDollar') },
      ),
      description: intl.formatMessage({
        id: 'lWjozgPJmAACct17k0kA',
        defaultMessage: 'Fresh ingredients and chef-designed recipes delivered to your door.',
        description: 'Partner promo offer description for HelloFresh meal kits in LuxPlus benefits',
      }),
    },
    [LuxPlusPartnerPromoClassifier.WINE_SELECTORS]: {
      title: intl.formatMessage({
        id: 'WgCLc5pSO6qeKlsDt9eB',
        defaultMessage: '50% off select cases',
        description: 'Partner promo offer title for Wine Selectors discount in LuxPlus benefits',
      }),
      description: intl.formatMessage({
        id: 'GRUEEuaBM4YEaiLZ98ob',
        defaultMessage: 'Premium wine cases curated by Australia’s leading wine experts.',
        description: 'Partner promo offer description for Wine Selectors in LuxPlus benefits',
      }),
    },
  }), [intl, formatCurrency])

  const offer = useAppSelector(getBaseTierOfferWithJoiningFee)

  const memberBenefits = useAppSelector(
    state => state.auth.account.luxPlus.member.subscription.item?.benefits,
  )

  const memberPartnerBenefits = useMemo(() => {
    if (!memberBenefits) return []
    return memberBenefits.filter(
      (b): b is App.MemberSubscriptionBenefitItem => b.type === 'partner_promo_code' && 'classifier' in b,
    )
  }, [memberBenefits])

  const openModalForClassifier = useCallback((classifier?: string, memberBenefit?: App.MemberSubscriptionBenefitItem) => {
    switch (classifier) {
      case LuxPlusPartnerPromoClassifier.TURO:
        showModal(<LuxPlusTuroCampaignModal benefit={memberBenefit} />)
        break
      case LuxPlusPartnerPromoClassifier.HELLOFRESH:
        showModal(<HelloFreshCampaignModal benefit={memberBenefit} />)
        break
      case LuxPlusPartnerPromoClassifier.WINE_SELECTORS:
        showModal(<WineSelectorsCampaignModal benefit={memberBenefit} />)
        break
    }
  }, [showModal])

  const viewOfferLabel = intl.formatMessage({
    id: 'CfHs17rh8uPFgL9S0yD5',
    defaultMessage: 'View offer',
    description: 'Call-to-action button label to view a partner promo offer in LuxPlus benefits',
  })
  const unlockOfferLabel = intl.formatMessage({
    id: 'MPKTQLKWxvPdJI20TqmC',
    defaultMessage: 'Unlock offer',
    description: 'Call-to-action button label to unlock a partner promo offer by becoming a LuxPlus member',
  })

  return useMemo(() => {
    if (!offer?.benefits) return []

    const offerPartnerBenefits = offer.benefits.filter(b => b.type === 'partner_promo_code')

    if (offerPartnerBenefits.length > 0) {
      return offerPartnerBenefits.map((benefit, index) => {
        const matchingMember = memberPartnerBenefits.find(mb => mb.classifier === benefit.classifier)
        const classifier = benefit.classifier
        const overrides = classifier ? partnerContentOverrides[classifier] : undefined
        const onViewDetails = canRedeemPartnerOffers
          ? (classifier ? () => openModalForClassifier(classifier, matchingMember) : undefined)
          : handleBecomeMember
        return {
          id: classifier ?? `partner-${index}`,
          title: overrides?.title ?? benefit.title,
          shortTitle: benefit.shortTitle,
          description: overrides?.description ?? benefit.description,
          logo: (classifier && PARTNER_LOGO_MAP[classifier]) ?? null,
          ctaLabel: canRedeemPartnerOffers ? viewOfferLabel : unlockOfferLabel,
          onViewDetails,
        }
      })
    }

    return memberPartnerBenefits.map(mb => {
      const overrides = mb.classifier ? partnerContentOverrides[mb.classifier] : undefined
      return {
        id: mb.classifier ?? mb.type,
        title: overrides?.title ?? mb.title,
        shortTitle: mb.shortTitle,
        description: overrides?.description ?? mb.description,
        logo: (mb.classifier && PARTNER_LOGO_MAP[mb.classifier]) ?? null,
        ctaLabel: viewOfferLabel,
        onViewDetails: () => openModalForClassifier(mb.classifier, mb),
      }
    })
  }, [offer?.benefits, memberPartnerBenefits, openModalForClassifier, canRedeemPartnerOffers, handleBecomeMember, partnerContentOverrides, viewOfferLabel, unlockOfferLabel])
}

export default usePartnerPromoOffers
